import { useEffect } from 'react'
import { useLocation } from 'react-router-dom'

declare global {
  interface Window {
    fbq: any
    _fbq: any
  }
}

export const META_PIXEL_ID = '1266729085649136'

/**
 * Installs the Meta Pixel using the EXACT canonical base snippet that
 * Meta Events Manager provides — injected verbatim so Meta recognizes
 * it as a standard pixel and classifies events (PageView, etc.) as
 * standard events rather than "custom".
 *
 * Mounted once at the app root (App.tsx). The base fires `PageView` on
 * initial load; this hook additionally fires `PageView` on client-side
 * route changes (SPA navigation). Action events (CompleteRegistration,
 * Search) are fired via `trackMetaEvent` from the relevant pages.
 */
export function useMetaPixel() {
  const location = useLocation()

  // Install the base pixel exactly once, using the verbatim snippet.
  useEffect(() => {
    if (document.getElementById('meta-pixel-base')) return

    const script = document.createElement('script')
    script.id = 'meta-pixel-base'
    script.type = 'text/javascript'
    // Verbatim Meta Pixel base code (identical to the Events Manager snippet).
    script.text = `
      !function(f,b,e,v,n,t,s)
      {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
      n.callMethod.apply(n,arguments):n.queue.push(arguments)};
      if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
      n.queue=[];t=b.createElement(e);t.async=!0;
      t.src=v;s=b.getElementsByTagName(e)[0];
      s.parentNode.insertBefore(t,s)}(window, document,'script',
      'https://connect.facebook.net/en_US/fbevents.js');
      fbq('init', '${META_PIXEL_ID}');
      fbq('track', 'PageView');
    `
    document.head.appendChild(script)

    // <noscript> fallback pixel.
    const noscript = document.createElement('noscript')
    noscript.id = 'meta-pixel-noscript'
    noscript.innerHTML = `<img height="1" width="1" style="display:none" src="https://www.facebook.com/tr?id=${META_PIXEL_ID}&ev=PageView&noscript=1" />`
    document.head.appendChild(noscript)

    console.log('[Meta Pixel] base installed + PageView', META_PIXEL_ID)
  }, [])

  // Fire PageView on each client-side route change (SPA navigation). The
  // initial PageView is fired by the base snippet above; this covers
  // subsequent in-app navigations to pages like /Request-Information.
  useEffect(() => {
    if (typeof window !== 'undefined' && window.fbq) {
      window.fbq('track', 'PageView')
      console.log('[Meta Pixel] PageView', location.pathname)
    }
  }, [location.pathname])
}

/**
 * Fire a standard Meta Pixel event (e.g. 'CompleteRegistration',
 * 'Search') through the initialized fbq. Logs the call for verification
 * in the console / Pixel Helper / Network (tr?id=…&ev=…).
 */
export function trackMetaEvent(
  event: string,
  params?: Record<string, unknown>,
  eventId?: string,
): void {
  if (typeof window === 'undefined') return
  if (window.fbq) {
    console.log(
      `[Meta Pixel] track ${event} (pixel ${META_PIXEL_ID})`,
      params || {},
      eventId ? `eventID: ${eventId}` : '',
    )
    if (eventId) {
      window.fbq('track', event, params, { eventID: eventId })
    } else {
      window.fbq('track', event, params)
    }
  } else {
    console.warn(`[Meta Pixel] window.fbq not available — ${event} NOT fired`)
  }
}
